import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Gavel, ShoppingBag, Clock, CheckCircle, Star, ChevronLeft, ChevronRight, Share2, Shield } from "lucide-react";
import { handleImgError } from "../lib/imageFallback";
import { shareProductWhatsApp } from "../lib/share";
import { useParticipations, participationLabel } from "../lib/participations";
import { WhatsAppBadge } from "./WhatsAppBadge";
import { PaymentInfoModal } from "./TopBar";
import QuickOfferPopup from "./QuickOfferPopup";
import ProductDetailsPopup from "./ProductDetailsPopup";

const SECTIONS = [
  { id: "direct", label: "Mejor Oferta", icon: ShoppingBag },
  { id: "auctions", label: "Subastas", icon: Gavel },
  { id: "scheduled", label: "Próximamente", icon: Clock },
  { id: "sold", label: "Vendidos", icon: CheckCircle },
  { id: "reviews", label: "Reviews", icon: Star },
];

const timeLeft = (endTime) => {
  if (!endTime) return null;
  const diff = new Date(endTime) - new Date();
  if (diff <= 0) return "Finalizada";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const timerColor = (endTime) => {
  if (!endTime) return "bg-black/60";
  const diff = new Date(endTime) - new Date();
  if (diff <= 0) return "bg-gray-600";
  if (diff <= 3600000) return "bg-red-500";
  if (diff <= 86400000) return "bg-amber-500";
  return "bg-emerald-500";
};

// ---- One banner-style product card with swipe: [info] [main photo] [photos...] ----
const BannerFeedCard = ({ product }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const scrollRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(1);
  const [popupMode, setPopupMode] = useState(null); // "direct_purchase" | "offer" | null
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [localBid, setLocalBid] = useState(null);
  const [localBidCount, setLocalBidCount] = useState(null);
  const { participations, markParticipation } = useParticipations(token);
  const participation = participations[product.product_id];
  const myBid = participation?.type === "bid" ? Math.max(localBid ?? 0, participation.amount ?? 0) : null;
  const isWinning = myBid != null && myBid >= (localBid ?? product.current_bid ?? product.price ?? 0);

  const photos = [product.image_url, ...(product.images || [])].filter(Boolean);
  const totalSlides = 1 + photos.length;
  const isAuction = product.sale_type === "auction";
  const isSold = product.is_sold;
  const isScheduled = product.is_scheduled;
  const isAuctionReserved = isAuction && product.status === "reserved" && !isSold;
  const hasPromo = !isAuction && !!product.promo_discount_percent && !!product.promo_price;
  const price = isAuction ? (localBid ?? product.current_bid ?? product.price) : (product.promo_price || product.buy_now_price || product.price);
  const bidCount = localBidCount ?? product.bid_count ?? 0;
  const remaining = isAuction ? timeLeft(product.auction_end_time) : null;

  useEffect(() => {
    // Start on the main photo (slide 1), details panel hidden to the left
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: el.clientWidth, behavior: "instant" });
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setSlideIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goToSlide = (i) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  const openBid = (e) => { e.stopPropagation(); setPopupMode("offer"); };
  const openInterest = (e) => { e.stopPropagation(); setPopupMode("direct_purchase"); };
  const openDetails = (e) => { e.stopPropagation(); setDetailsOpen(true); };

  const handleOfferSuccess = (data) => {
    if (data?.type === "bid") {
      const amt = data?.amount;
      if (amt) { setLocalBid(amt); setLocalBidCount((product.bid_count || 0) + 1); }
      markParticipation(product.product_id, { type: "bid", amount: amt || 0 });
    } else {
      markParticipation(product.product_id, { type: "intent" });
    }
  };

  const ActionButtons = ({ full = false, onLight = false }) => {
    if (isSold) return <div className={`${full ? "w-full" : ""} px-4 py-2 rounded-full bg-gray-500/80 text-white text-xs font-bold text-center`}>VENDIDO</div>;
    if (isAuctionReserved) return (
      <div className={`${full ? "w-full" : ""} px-4 py-2 rounded-full bg-gray-500/80 text-white text-xs font-bold text-center`} data-testid={`feed-reserved-btn-${product.product_id}`}>
        SUBASTA FINALIZADA · RESERVADO
      </div>
    );
    if (isScheduled) return <div className={`${full ? "w-full" : ""} px-4 py-2 rounded-full bg-amber-500/90 text-white text-xs font-bold text-center`}>PRÓXIMAMENTE</div>;
    const secondaryCls = onLight
      ? "flex-1 px-3 py-2 rounded-full bg-white border border-[#CBD5E1] text-[#0F172A] text-xs font-bold"
      : "flex-1 px-3 py-2 rounded-full bg-white/25 backdrop-blur-md border border-white/40 text-white text-xs font-bold";
    return (
      <div className={`flex gap-2 ${full ? "w-full" : ""}`}>
        {isAuction ? (
          <>
            <button
              onClick={openBid}
              className={`flex-1 px-3 py-2 rounded-full text-white text-xs font-bold ${participation?.type === "bid" ? (isWinning ? "bg-sky-500" : "bg-amber-500") : "bg-emerald-500"}`}
              data-testid={`feed-bid-btn-${product.product_id}`}
            >
              {participation?.type === "bid" ? (isWinning ? "¡Ganando!" : "Subir tu BiiD") : "Participar"}
            </button>
            <button onClick={openDetails} className={secondaryCls} data-testid={`feed-details-btn-${product.product_id}`}>
              Detalles
            </button>
          </>
        ) : participation ? (
          <>
            <div
              className="flex-1 px-3 py-2 rounded-full bg-white/20 border border-white/30 text-white/80 text-xs font-bold text-center backdrop-blur-sm"
              data-testid={`feed-pending-btn-${product.product_id}`}
            >
              Solicitud enviada
            </div>
            <button onClick={openDetails} className={secondaryCls} data-testid={`feed-details-btn-${product.product_id}`}>
              Detalles
            </button>
          </>
        ) : (
          <>
            <button onClick={openInterest} className="flex-1 px-3 py-2 rounded-full bg-emerald-500 text-white text-xs font-bold" data-testid={`feed-buy-btn-${product.product_id}`}>
              Me interesa
            </button>
            <button onClick={openDetails} className={secondaryCls} data-testid={`feed-details-btn-${product.product_id}`}>
              Detalles
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#0F172A] shadow-sm" style={{ aspectRatio: "1/1" }} data-testid={`banner-card-${product.product_id}`}>
      {/* Swipeable slides: horizontal only, vertical gestures pass through to page scroll */}
      <div ref={scrollRef} onScroll={onScroll} className="absolute inset-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth", overscrollBehaviorX: "contain", touchAction: "pan-x pan-y" }}>
        {/* Slide 0: Details panel (photo stays as blurred backdrop; title/price/buttons persist below) */}
        <div className="flex-shrink-0 w-full h-full snap-center relative bg-[#0F172A]" onClick={() => navigate(`/product/${product.product_id}`)}>
          <img src={product.image_url} alt="" aria-hidden="true" onError={handleImgError} className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
          <div className="absolute left-4 right-4 bottom-16 flex flex-col" style={{ top: "calc(38% + 34px)" }}>
            <p className="text-white/90 text-xs leading-relaxed overflow-hidden flex-1">{product.description}</p>
            <div className="mt-2 space-y-1.5">
              <button
                onClick={(e) => { e.stopPropagation(); setPolicyOpen(true); }}
                className="w-full flex items-center justify-center gap-1.5 bg-[#FEF3C7]/95 rounded-lg px-2.5 py-1.5"
                data-testid={`feed-cod-badge-${product.product_id}`}
              >
                <Shield size={12} className="text-[#D97706] flex-shrink-0" />
                <span className="text-[10px] text-[#D97706] font-medium whitespace-nowrap truncate">
                  Pago contraentrega: revisas y luego pagas →
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Slides 1..n: Photos */}
        {photos.map((img, i) => (
          <div key={i} className="flex-shrink-0 w-full h-full snap-center relative" onClick={() => navigate(`/product/${product.product_id}`)}>
            <img src={img} alt={`${product.title} ${i + 1}`} loading="lazy" onError={handleImgError} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      {/* Persistent overlays: badge, title, price top-left; timer + bid count top-right at title height */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/40 to-transparent pt-2.5 pb-8 px-2.5 pointer-events-none">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <span className={`inline-block text-[9px] font-bold px-2 py-1 rounded-full text-white ${isSold ? "bg-gray-600" : isScheduled ? "bg-amber-500" : isAuction ? "bg-emerald-500" : "bg-orange-500"}`}>
              {isSold ? "VENDIDO" : isScheduled ? "PRÓXIMAMENTE" : isAuction ? "SUBASTA" : "VENTA"}
            </span>
            {participation && !isAuction && (
              <span className="inline-flex items-center gap-1 ml-1.5 text-[9px] font-bold px-2 py-1 rounded-full bg-sky-500 text-white" data-testid={`feed-participation-badge-${product.product_id}`}>
                <CheckCircle size={9} /> {participationLabel(participation)}
              </span>
            )}
            <h3 className="text-white font-bold text-sm leading-tight line-clamp-1 mt-2.5">{product.title}</h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {hasPromo && (
                <span className="text-white/60 text-xs line-through">${product.price?.toLocaleString()}</span>
              )}
              <span className="text-white text-lg font-bold">${price}</span>
              {hasPromo && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid={`feed-discount-badge-${product.product_id}`}>
                  -{product.promo_discount_percent}%
                </span>
              )}
            </div>
          </div>
          {isAuction && (
            <div className="flex flex-col items-end gap-1 mt-9">
              {remaining && !isSold && (
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full text-white backdrop-blur-sm ${timerColor(product.auction_end_time)}`}>⏱ {remaining}</span>
              )}
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/90 text-white backdrop-blur-sm">
                {bidCount} pujas
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Share via WhatsApp */}
      <button
        onClick={(e) => { e.stopPropagation(); shareProductWhatsApp(product, e); }}
        className="absolute right-2.5 top-2.5 z-10 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center"
        data-testid={`feed-share-btn-${product.product_id}`}
        aria-label="Compartir por WhatsApp"
      >
        <Share2 size={13} />
      </button>
      {/* Swipe hints: single persistent "Info" pill — never unmounts, so it stays perfectly still */}
      <button
        onClick={(e) => { e.stopPropagation(); if (slideIndex >= 1) goToSlide(0); }}
        className="absolute left-1.5 top-[38%] z-10 flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-black/55 backdrop-blur-md text-white text-[9px] font-semibold"
        data-testid={`feed-details-hint-${product.product_id}`}
      >
        <ChevronLeft size={11} /> Info
      </button>
      {slideIndex === 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goToSlide(1); }}
          className="absolute right-1.5 top-[38%] z-10 flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-black/55 backdrop-blur-md text-white text-[9px] font-semibold"
          data-testid={`feed-photos-hint-${product.product_id}`}
        >
          Fotos <ChevronRight size={11} />
        </button>
      )}
      {slideIndex >= 1 && slideIndex < totalSlides - 1 && photos.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); goToSlide(slideIndex + 1); }} className="absolute right-1.5 top-[38%] z-10 p-1 rounded-full bg-white/25 backdrop-blur-md text-white">
          <ChevronRight size={13} />
        </button>
      )}
      {/* Bottom gradient: action buttons only — same size and position on every slide */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/85 to-transparent pt-8 pb-3 px-3 pointer-events-none">
        <div className="pointer-events-auto">
          <ActionButtons />
        </div>
      </div>

      {/* Carousel dots */}
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 flex gap-1 pointer-events-none">
        {Array.from({ length: totalSlides }).map((_, i) => (
          <span key={i} className={`rounded-full transition-all ${i === slideIndex ? "bg-white w-3 h-1" : "bg-white/50 w-1 h-1"}`} />
        ))}
      </div>

      {/* Quick offer / interest popup (works for guests too) */}
      <QuickOfferPopup
        isOpen={popupMode !== null}
        onClose={() => setPopupMode(null)}
        product={product}
        mode={popupMode || "offer"}
        onSuccess={handleOfferSuccess}
      />
      <ProductDetailsPopup
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        product={product}
        participation={participation}
        onSuccess={handleOfferSuccess}
      />
      <PaymentInfoModal open={policyOpen} onOpenChange={setPolicyOpen} />
    </div>
  );
};

// ---- Small review strip between products ----
const ReviewStrip = ({ review }) => {
  if (!review) return null;
  return (
    <div className="rounded-2xl border border-amber-200/70 bg-white overflow-hidden shadow-sm" data-testid="review-strip">
      <div className="flex items-center justify-between px-3 py-1.5 bg-amber-50/90 border-b border-amber-100">
        <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide flex items-center gap-1">
          <Star size={10} className="text-amber-400 fill-amber-400" /> Opinión de cliente
        </span>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={10} className={i < review.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"} />
          ))}
        </div>
      </div>
      <div className="px-3 py-2.5">
        <p className="text-xs text-[#374151] italic leading-relaxed">"{review.comment}"</p>
        <p className="text-[10px] text-[#64748B] font-semibold mt-1.5 flex items-center gap-1">
          {review.reviewer_auth_method === "whatsapp" && <WhatsAppBadge size="xs" />}
          — {review.reviewer_name || review.user_name}
        </p>
        {review.product_title && (
          <p className="text-[10px] text-[#94A3B8] mt-0.5">Compró: {review.product_title}</p>
        )}
      </div>
    </div>
  );
};

// ---- Full review card for the Reviews section ----
const ReviewCard = ({ review }) => (
  <div className="bg-white rounded-xl p-3 border border-[#E2E8F0]">
    <div className="flex items-center justify-between mb-1">
      <span className="font-semibold text-[#0F172A] text-xs flex items-center gap-1">
        {review.reviewer_name || review.user_name}
        {review.reviewer_auth_method === "whatsapp" && <WhatsAppBadge size="xs" />}
      </span>
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={11} className={i < review.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"} />
        ))}
      </div>
    </div>
    <p className="text-xs text-[#374151]">{review.comment}</p>
    {review.product_title && <p className="text-[10px] text-[#94A3B8] mt-1">Compró: {review.product_title}</p>}
  </div>
);

// ---- Main mobile banner feed ----
export const BannerFeed = ({ auctionProducts, directProducts, scheduledProducts, soldProducts, reviews }) => {
  // If there are no active auctions, default to "Mejor Oferta" so the feed isn't blank on load
  const [activeSection, setActiveSection] = useState(() =>
    (!auctionProducts || auctionProducts.length === 0) && directProducts && directProducts.length > 0 ? "direct" : "auctions"
  );
  const [slideDir, setSlideDir] = useState("right");
  const [tick, setTick] = useState(0);
  const [headerH, setHeaderH] = useState(0);

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      setHeaderH(header ? Math.round(header.getBoundingClientRect().height) : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 1000);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const changeSection = (id) => {
    const oldIdx = SECTIONS.findIndex((s) => s.id === activeSection);
    const newIdx = SECTIONS.findIndex((s) => s.id === id);
    setSlideDir(newIdx > oldIdx ? "right" : "left");
    setActiveSection(id);
  };

  const productsBySection = {
    auctions: auctionProducts,
    direct: directProducts,
    scheduled: scheduledProducts,
    sold: soldProducts,
  };

  const items = productsBySection[activeSection] || [];

  return (
    <div className="md:hidden" data-testid="banner-feed">
      {/* Sticky section pills (banner scrolls away, these stay) */}
      <div className="sticky z-30 bg-[#FAFAF9]/95 backdrop-blur-sm py-2 -mx-0" style={{ top: headerH }} data-testid="feed-section-pills">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => changeSection(s.id)}
              className={`flex-shrink-0 flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                activeSection === s.id
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-[#64748B] border-[#E2E8F0]"
              }`}
              data-testid={`feed-pill-${s.id}`}
            >
              <s.icon size={12} />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feed with slide animation on section change */}
      <div key={activeSection} className={`px-4 pt-1 pb-4 space-y-3 ${slideDir === "right" ? "feed-slide-right" : "feed-slide-left"}`}>
        {activeSection === "reviews" ? (
          reviews.length === 0 ? (
            <p className="text-center text-sm text-[#94A3B8] py-10">Aún no hay reviews</p>
          ) : (
            reviews.map((r) => <ReviewCard key={r.review_id} review={r} />)
          )
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-[#94A3B8] py-10">No hay productos en esta sección</p>
        ) : (
          items.map((product, idx) => (
            <div key={product.product_id} className="space-y-3">
              <BannerFeedCard product={product} />
              {reviews.length > 0 && idx % 2 === 1 && idx < items.length - 1 && (
                <ReviewStrip review={reviews[Math.floor(idx / 2) % reviews.length]} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BannerFeed;
